/*--------------------------------------------------------------------------------------------
Program     :  TopBar.js
Description :  Main program for Map and Track Top date Display
Date        :  29/08/2024 
Created By  :  Fode/Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
29082024        - Fode/Jelbin     -  29/08/2024        Created
---------------------------------------------------------------------------------------------*/
import React, { useState, useEffect } from 'react';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const TopBar = ({ dateRange, onDateChange, onVisibilityChange }) => {
    const [visibleDates, setVisibleDates] = useState([]);
    const [visibility, setVisibility] = useState({});
    const [currentStartIndex, setCurrentStartIndex] = useState(0);


    useEffect(() => {
        if (dateRange.start && dateRange.end) {
            const allDates = generateDateRange(dateRange.start, dateRange.end);
            const initialVisibility = {};
            allDates.forEach((date, index) => {
                const dateKey = date.toISOString().split('T')[0];
                initialVisibility[dateKey] = index === 0 ? 'visible' : 'hidden';
            });
            setVisibility(initialVisibility);
            onVisibilityChange(initialVisibility);

            // Set initial visible dates
            setVisibleDates(allDates.slice(0, 7));
            setCurrentStartIndex(0);
        }
    }, [dateRange, onVisibilityChange]);

    const generateDateRange = (start, end) => {
        let dates = [];
        let currentDate = new Date(start);
        const endDate = new Date(end);
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
    };

    const scrollDates = (direction) => {
        const allDates = generateDateRange(dateRange.start, dateRange.end);
        let newStartIndex = direction === 'left'
            ? Math.max(0, currentStartIndex - 7)
            : Math.min(allDates.length - 7, currentStartIndex + 7);
        setVisibleDates(allDates.slice(newStartIndex, newStartIndex + 7));
        setCurrentStartIndex(newStartIndex);
    };

    const toggleVisibility = (date, option) => {
        setVisibility(prev => {
            const newVisibility = { ...prev };
            const dateKey = date.toISOString().split('T')[0];
            
            if (option === 'visible') {
                // Set all other dates to their previous state or hidden
                Object.keys(newVisibility).forEach(key => {
                    if (key !== dateKey) {
                        newVisibility[key] = newVisibility[key] === 'blur' ? 'blur' : 'hidden';
                    }
                });
                newVisibility[dateKey] = 'visible';
            } else {
                // If changing the currently visible date to blur or hidden
                if (newVisibility[dateKey] === 'visible') {
                    // Find the first non-hidden date to make visible
                    const firstNonHiddenKey = Object.keys(newVisibility).find(key => 
                        newVisibility[key] !== 'hidden' && key !== dateKey
                    );
                    if (firstNonHiddenKey) {
                        newVisibility[firstNonHiddenKey] = 'visible';
                    } else {
                        // If all others are hidden, keep this one visible
                        return prev;
                    }
                }
                newVisibility[dateKey] = option;
            }

            onVisibilityChange(newVisibility);
            return newVisibility;
        });
    };

    const formatDate = (date) => {
        const dayName = date.toLocaleString('default', { weekday: 'short' });
        const dayNumber = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        return `${dayName}\n${dayNumber}\n${month}`;
    };

    const handleAlignment = (date, newAlignment) => {
        if (newAlignment !== null) {
            toggleVisibility(date, newAlignment);
        }
    };
    
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f3f4f6' }}>
            <button
                onClick={() => scrollDates('left')}
                style={{ padding: '8px'/*, backgroundColor: '#3b82f6'*/, color: 'black', border: 'none', borderRadius: '0px' }}
                disabled={visibleDates[0]?.getTime() === new Date(dateRange.start).getTime()}
            >
                <ArrowBackIosIcon />
            </button>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'space-around' }}>
                {visibleDates.map(date => {
                    const dateKey = date.toISOString().split('T')[0];
                    return (
                        <div key={dateKey} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', /*whiteSpace: 'pre-line',*/ textAlign: 'center' }}>
                                {formatDate(date)}
                            </span>
                            <div style={{ display: 'flex', marginTop: '8px' }}>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={visibility[dateKey] || 'hidden'}
                                    exclusive
                                    onChange={(event, newAlignment) => handleAlignment(date, newAlignment)}
                                    aria-label="visibility"
                                    size="small"
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            color: '#1565C0',
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #1565C0',
                                            '&:hover': {
                                                backgroundColor: '#ffffff',
                                                color: '#1565C0',
                                            },
                                            '&.Mui-selected': {
                                                color: '#ffffff',
                                                backgroundColor: '#1565C0',
                                                border: '1px solid #1565C0',
                                                '&:hover': {
                                                    backgroundColor: '#1565C0',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="visible" aria-label="visible">
                                        <VisibilityIcon />
                                    </ToggleButton>
                                    <ToggleButton value="blur" aria-label="blur">
                                        <BlurOnIcon />
                                    </ToggleButton>
                                    <ToggleButton value="hidden" aria-label="hidden">
                                        <VisibilityOffIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button
                onClick={() => scrollDates('right')}
                style={{ padding: '8px', color: 'black', borderRadius: '0px', border: 'none'/*backgroundColor: '#3b82f6', borderRadius: '4px'*/ }}
                disabled={visibleDates[6]?.getTime() === new Date(dateRange.end).getTime()}
            >
                <ArrowForwardIosIcon />
            </button>
        </div>
    );
};

export default TopBar;